import { computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import FindDoc from "cc-frontend/helpers/find-doc"
import type EmberStore from "cc-frontend/services/ember-store"
import type Rpc from "cc-frontend/services/rpc"
import { use } from "ember-could-get-used-to-this"
import { isNil, isString } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!
import { tracked } from "tracked-built-ins"

interface Signature {
  Element: HTMLDivElement
  Args: {
    jurisdictionId: string
  }
}

export default class AdminCspJurisdiction extends Component<Signature> {
  @service("ember-store") declare store: EmberStore
  @service declare rpc: Rpc
  @use jurisdiction = new FindDoc(() => ["standard-jurisdiction", this.args.jurisdictionId])
  @tracked isSaving: boolean = false
  @tracked isMenuOpen: boolean = false

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen
  }

  @computed("jurisdiction.content.internalVersion")
  get standardSetGroups() {
    if (isNil(this.jurisdiction.content)) return []
    return R.pipe(
      this.jurisdiction.content?.attributes.standardSets,
      R.sortBy((standardSet) => standardSet.subject),
      R.groupBy((standardSet) => {
        return [standardSet.subject, standardSet.document?.publicationStatus]
      }),
      R.toPairs,
      R.map(([key, standardSets]) => {
        // Because all the standard sets have the same subject and status, we can just grab the first one
        return {
          id: key,
          subject: standardSets[0].subject,
          status: standardSets[0].document?.publicationStatus,
          standardSets: R.sortBy(standardSets, (s) => s.title),
        }
      })
    )
  }

  changeTitle = () => {
    let title = window.prompt("Enter the new title", this.jurisdiction.content?.attributes.title)
    if (isString(title) === true && title !== null && title !== undefined) {
      this.isSaving = true
      this.rpc
        .admin_csp_edit_jurisdiction({
          fieldMask: ["title"],
          jurisdictionIds: [this.args.jurisdictionId],
          title: title,
        })
        .then((response) => {
          this.isSaving = false
          if (response.responseOk === false) {
            console.log(response)
            window.alert(`Sorry! There was an error! ${response.result.message}`)
          } else {
            this.store.find("standard-jurisdiction", this.args.jurisdictionId, true)
          }
        })
    }
  }

  changeStatus = (status) => {
    this.isSaving = true
    this.rpc
      .admin_csp_edit_jurisdiction({
        fieldMask: ["status"],
        jurisdictionIds: [this.args.jurisdictionId],
        status: status,
      })
      .then((response) => {
        this.isSaving = false
        if (response.responseOk === false) {
          console.log(response)
          window.alert(`Sorry! There was an error! ${response.result.message}`)
        } else {
          this.store.find("standard-jurisdiction", this.args.jurisdictionId, true)
        }
      })
  }

  changeType = (type) => {
    this.isSaving = true
    this.rpc
      .admin_csp_edit_jurisdiction({
        fieldMask: ["type"],
        jurisdictionIds: [this.args.jurisdictionId],
        type: type,
      })
      .then((response) => {
        this.isSaving = false
        if (response.responseOk === false) {
          console.log(response)
          window.alert(`Sorry! There was an error! ${response.result.message}`)
        } else {
          this.store.find("standard-jurisdiction", this.args.jurisdictionId, true)
        }
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Admin::Csp::Jurisdiction": typeof AdminCspJurisdiction
  }
}
